import React, { useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import {
  Rocket, KeyRound, Copy, Check, Terminal, ListChecks, ArrowRight, ShieldCheck
} from 'lucide-react';
import { participantApiBase } from '../utils/sandboxEndpoints';

function CodeBlock({ children }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(children);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="relative">
      <button onClick={copy}
        className="absolute top-2 right-2 flex items-center gap-1 px-2 py-1 text-xs rounded-md bg-gray-800 border border-gray-700 text-gray-300 hover:bg-gray-700">
        {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
        {copied ? 'Copied' : 'Copy'}
      </button>
      <pre className="bg-gray-900 text-gray-100 text-xs rounded-lg p-4 pr-16 overflow-x-auto whitespace-pre-wrap">{children}</pre>
    </div>
  );
}

const AUTH = [
  ['X-API-Key', 'Every API request', 'Your tenant API key. Identifies you to the sandbox and routes to your isolated schema.'],
  ['X-Platform-Key', 'Provisioning only', 'Platform-admin key held by the operator — used to create tenants and issue API keys. You will not normally hold this.'],
  ['Message signature', 'Signed payloads', 'XMLDSig (ISO 20022) or JWS (FSPIOP) on the message itself — separate from transport auth. See the signing guide for your corridor.'],
];

export default function GettingStarted() {
  const location = useLocation();
  const isIso = location.pathname.startsWith('/iso20022');
  const [corridor, setCorridor] = useState(isIso ? 'iso' : 'fspiop');

  // The address the participant's systems call: this portal's, which the edge
  // routes to each engine (FSPIOP at /api, ISO 20022 at /iso20022/api).
  const FSPIOP_BASE = participantApiBase('FSPIOP');
  const ISO_BASE = participantApiBase('ISO20022');
  const signingHref = isIso ? '/iso20022/signing-guide' : '/guides/signing';

  return (
    <div className="space-y-8 max-w-4xl">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Rocket className="w-6 h-6 text-primary-600" />
          Getting Started
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          From zero to your first authenticated call against the sandbox. Authentication is shared
          across corridors; the endpoints differ.
        </p>
      </div>

      {/* What you'll need */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <ListChecks className="w-5 h-5 text-gray-400" /> What you'll need
        </h2>
        <ul className="space-y-2">
          {[
            ['A tenant', 'The sandbox operator provisions a tenant for your institution and issues your first API key.'],
            ['Portal login', 'A username and password (plus your Tenant ID, in multi-tenant deployments) to sign in here.'],
            ['An API key', 'The X-API-Key you send on every API request. Keep it secret — it is shown only once when issued.'],
            ['A signing key', 'An RSA key + certificate to sign messages. See the signing guide for your corridor.'],
          ].map(([t, d]) => (
            <li key={t} className="flex gap-3 bg-white border border-gray-200 rounded-lg p-3">
              <Check className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
              <div><span className="text-sm font-medium text-gray-900">{t}</span>
                <span className="text-sm text-gray-600"> — {d}</span></div>
            </li>
          ))}
        </ul>
      </section>

      {/* Auth model */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <KeyRound className="w-5 h-5 text-gray-400" /> Authentication model
        </h2>
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <tbody>
              {AUTH.map(([k, when, desc], i) => (
                <tr key={k} className={i % 2 ? 'bg-gray-50' : ''}>
                  <td className="px-4 py-2.5 font-mono text-xs text-primary-700 align-top whitespace-nowrap">{k}</td>
                  <td className="px-4 py-2.5 align-top w-36 text-xs text-gray-500">{when}</td>
                  <td className="px-4 py-2.5 align-top text-sm text-gray-700">{desc}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Get an API key */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">Getting your API key</h2>
        <p className="text-sm text-gray-600 mb-3">
          The operator provisions your tenant and returns its first API key (shown once). For
          reference, that call looks like:
        </p>
        <CodeBlock>{`curl -X POST ${FSPIOP_BASE}/api/platform/tenants \\
  -H "X-Platform-Key: <platform-admin-key>" \\
  -H "Content-Type: application/json" \\
  -d '{ "tenantId": "mtn-rw", "fspId": "MTNRW", "displayName": "MTN Rwanda" }'

# → 201 Created
# { "tenantId": "mtn-rw", "apiKey": "…shown once…", "status": "ACTIVE" }`}</CodeBlock>
        <p className="text-sm text-gray-500 mt-2">
          Store the returned <span className="font-mono text-xs">apiKey</span> securely. If lost, the
          operator must reset it — the original is never shown again.
        </p>
      </section>

      {/* First call */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <Terminal className="w-5 h-5 text-gray-400" /> Your first call
        </h2>

        {/* corridor toggle */}
        <div className="flex rounded-lg border border-gray-200 overflow-hidden text-sm font-medium w-fit mb-3">
          {[['iso', 'ISO 20022'], ['fspiop', 'FSPIOP']].map(([id, label]) => (
            <button key={id} onClick={() => setCorridor(id)}
              className={`px-4 py-1.5 transition-colors ${corridor === id ? 'bg-primary-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
              {label}
            </button>
          ))}
        </div>

        {corridor === 'iso' ? (
          <div className="space-y-3">
            <p className="text-sm text-gray-600">1. Confirm connectivity — list the registered message schemas:</p>
            <CodeBlock>{`curl ${ISO_BASE}/api/schemas \\
  -H "X-API-Key: <your-api-key>"`}</CodeBlock>
            <p className="text-sm text-gray-600">2. Validate a message — generate &amp; XSD-check a pacs.008:</p>
            <CodeBlock>{`curl -X POST "${ISO_BASE}/api/generate/validate/pacs.008" \\
  -H "X-API-Key: <your-api-key>" \\
  -H "Content-Type: application/json" \\
  -d '{ "grpHdr": { "msgId": "M1", "creDtTm": "2026-06-24T09:00:00Z",
        "nbOfTxs": "1", "sttlmInf": { "sttlmMtd": "CLRG" } },
        "cdtTrfTxInf": [{ "pmtId": { "endToEndId": "E2E-1", "txId": "TX-1" },
        "intrBkSttlmAmt": { "value": "1000.00", "ccy": "RWF" },
        "chrgBr": "SLEV", "dbtr": { "nm": "Alice" }, "cdtr": { "nm": "Bob" } }] }'`}</CodeBlock>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-gray-600">1. Confirm the active protocol:</p>
            <CodeBlock>{`curl ${FSPIOP_BASE}/api/switch/protocol \\
  -H "X-API-Key: <your-api-key>"

# → { "protocol": "MOJALOOP_FSPIOP_V1_1", "description": "…" }`}</CodeBlock>
            <p className="text-sm text-gray-600">2. Resolve which FSP owns an account:</p>
            <CodeBlock>{`curl -X POST "${FSPIOP_BASE}/api/switch/parties/lookup" \\
  -H "X-API-Key: <your-api-key>" \\
  -H "Content-Type: application/json" \\
  -d '{ "partyIdType": "MSISDN", "partyIdValue": "250788123456" }'

# → 202 Accepted, then poll GET /api/switch/status/{correlationId}`}</CodeBlock>
          </div>
        )}
      </section>

      {/* Next steps */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">Next steps</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Link to={signingHref}
            className="flex items-center justify-between bg-white border border-gray-200 rounded-lg p-4 hover:border-primary-400 group">
            <div className="flex items-center gap-3">
              <ShieldCheck className="w-5 h-5 text-primary-600" />
              <span className="text-sm font-medium text-gray-900">Signing Guide</span>
            </div>
            <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-primary-600" />
          </Link>
          <Link to={isIso ? '/iso20022/simulator' : '/guides/flow-simulator'}
            className="flex items-center justify-between bg-white border border-gray-200 rounded-lg p-4 hover:border-primary-400 group">
            <div className="flex items-center gap-3">
              <Rocket className="w-5 h-5 text-primary-600" />
              <span className="text-sm font-medium text-gray-900">Flow Simulator</span>
            </div>
            <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-primary-600" />
          </Link>
        </div>
      </section>
    </div>
  );
}
